import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { StoreBadges } from "@/components/landing/StoreBadges";
import { getPosts } from "@/data/blogPosts";
import { useLang } from "@/i18n/LanguageContext";

export default function StyleGuidePage({ texts, clusters, ldId, testPrefix, crossLink }) {
  const { t, lang } = useLang();
  const x = texts[lang] || texts.de;
  const b = t.blog;
  const posts = getPosts(lang);
  const bySlug = Object.fromEntries(posts.map((p) => [p.slug, p]));
  const badge = (p) => (p.audience === "men" ? b.catMen : p.audience === "women" ? b.catWomen : b.catB2C);

  useEffect(() => {
    document.title = x.metaTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", x.metaDescription);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = ldId;
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: x.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(ld);
    window.scrollTo(0, 0);
    return () => document.getElementById(ldId)?.remove();
  }, [x, ldId]);

  return (
    <main style={{ background: "var(--surface)" }} data-testid={`${testPrefix}-page`}>
      <Navbar />
      <header className="mx-auto max-w-5xl px-5 pb-6 pt-32 sm:px-8 sm:pt-40">
        <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
          <Star size={11} /> {x.kicker}
        </p>
        <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" style={{ color: "var(--on-surface)" }} data-testid={`${testPrefix}-h1`}>
          {x.h1}
        </h1>
        <p className="mt-6 max-w-2xl text-lg" style={{ color: "var(--muted-warm, #6B615B)" }}>{x.intro}</p>
      </header>

      {clusters.map((cluster, ci) => (
        <section key={cluster.key} className="mx-auto max-w-5xl px-5 py-10 sm:px-8" data-testid={`${testPrefix}-cluster-${cluster.key}`}>
          <Reveal>
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold" style={{ color: "var(--on-surface)" }}>
              <span style={{ color: "var(--gold)" }}>{String(ci + 1).padStart(2, "0")}</span> · {x.sections[cluster.key].h}
            </h2>
            <p className="mt-3 max-w-2xl text-base" style={{ color: "var(--muted-warm, #6B615B)" }}>{x.sections[cluster.key].p}</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {cluster.slugs.map((slug, i) => {
              const p = bySlug[slug];
              if (!p) return null;
              return (
                <Reveal key={slug} delay={i * 0.08}>
                  <Link
                    to={`/blog/${slug}`}
                    data-testid={`${testPrefix}-card-${slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border bg-white transition-shadow duration-500 hover:shadow-[0_24px_48px_-28px_rgba(44,39,37,0.4)]"
                    style={{ borderColor: "var(--line, #EAE1DB)" }}
                  >
                    <div className="h-40 overflow-hidden">
                      <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <span className="text-[10px] uppercase font-bold" style={{ color: "var(--camel)", letterSpacing: "0.1em" }}>
                        {badge(p)} · {p.readTime} {b.min}
                      </span>
                      <h3 className="mt-2 flex-1 font-serif-display text-lg font-semibold leading-snug" style={{ color: "var(--on-surface)" }}>{p.title}</h3>
                      <span className="mt-3 flex items-center gap-1.5 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--camel)" }}>
                        {b.readMore} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      ))}

      <section className="mx-auto max-w-5xl px-5 py-10 sm:px-8" data-testid={`${testPrefix}-faq`}>
        <Reveal>
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold" style={{ color: "var(--on-surface)" }}>FAQ</h2>
          <div className="mt-6 space-y-5">
            {x.faq.map((f, i) => (
              <div key={i} className="rounded-2xl border bg-white p-6" style={{ borderColor: "var(--line, #EAE1DB)" }}>
                <h3 className="font-semibold" style={{ color: "var(--on-surface)" }}>{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm, #6B615B)" }}>{f.a}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-24 pt-6 sm:px-8">
        <Reveal>
          <div className="rounded-[32px] px-8 py-12 text-center sm:px-14" style={{ background: "var(--on-surface)" }} data-testid={`${testPrefix}-cta`}>
            <h2 className="font-serif-display text-3xl font-semibold text-white">{x.ctaTitle}</h2>
            <p className="mx-auto mt-3 max-w-xl text-base" style={{ color: "rgba(252,250,248,0.65)" }}>{x.ctaSub}</p>
            <div className="mt-7 flex justify-center">
              <StoreBadges height={46} />
            </div>
          </div>
          {crossLink && (
            <p className="mt-8 text-center">
              <Link to={crossLink.to} data-testid={`${testPrefix}-crosslink`} className="gold-underline text-sm font-medium" style={{ color: "var(--camel)" }}>
                {crossLink.labels[lang] || crossLink.labels.de} →
              </Link>
            </p>
          )}
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
